import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ReportView } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import type { ReliefBoardResponse } from '../types';
import { ReliefBoardReport } from './ReliefBoardReport';
import { reliefBoardBackPath, reliefBoardListApiUrl } from './reliefBoardPrintMeta';

const ReliefBoardReportPage: React.FC = () => {
  const { colors } = useTheme();
  const [searchParams] = useSearchParams();

  const listUrl = useMemo(() => reliefBoardListApiUrl(searchParams), [searchParams]);
  const { data, error, isLoading } = useSWR<ReliefBoardResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const rankId = searchParams.get('rankId')?.trim() ?? '';
  const tab = searchParams.get('tab')?.trim() ?? '';
  const q = searchParams.get('q')?.trim() ?? '';

  const { data: lookups } = useSWR<{
    ranks: Array<{ id: number; name: string; abrv?: string | null }>;
  }>('/crew/lookups', fetcher, { revalidateOnFocus: false });

  const rankName = useMemo(() => {
    if (!rankId) return null;
    const match = (lookups?.ranks ?? []).find((r) => String(r.id) === rankId);
    return match?.name || match?.abrv || `Rank #${rankId}`;
  }, [lookups?.ranks, rankId]);

  const rows = useMemo(() => {
    let list = data?.rows ?? [];
    if (tab === 'nr') list = list.filter((r) => r.notRecommended);
    const query = q.toLowerCase();
    if (query) {
      list = list.filter(
        (r) =>
          r.name.toLowerCase().includes(query) ||
          (r.crewRef && r.crewRef.toLowerCase().includes(query)) ||
          (r.rank && r.rank.toLowerCase().includes(query)) ||
          (r.lastVessel && r.lastVessel.toLowerCase().includes(query)),
      );
    }
    return [...list].sort((a, b) => {
      const an = (a.name || '').toLowerCase();
      const bn = (b.name || '').toLowerCase();
      if (an !== bn) return an.localeCompare(bn);
      return a.crewId - b.crewId;
    });
  }, [data?.rows, q, tab]);

  const backTo = reliefBoardBackPath();
  const filterLabels = useMemo(
    () => ({
      rank: rankName,
      tab: tab === 'nr' ? 'NR only' : null,
      search: q || null,
    }),
    [q, rankName, tab],
  );

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading Relief board…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className={cn(
          'flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4',
          colors.background,
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>Could not load relief board.</p>
        <Link to={backTo} className={cn('text-sm font-medium hover:underline', colors.primaryFg)}>
          ← Back to Relief board
        </Link>
      </div>
    );
  }

  const subtitleParts = [
    rankName || 'All ranks',
    filterLabels.tab,
    `${rows.length} row${rows.length === 1 ? '' : 's'}`,
  ].filter(Boolean);

  return (
    <ReportView
      backTo={backTo}
      backLabel="Back to Relief board"
      title="Relief board"
      subtitle={subtitleParts.join(' · ')}
      printPageSize="A4 landscape"
      printMargin="0"
    >
      <ReliefBoardReport data={data} rows={rows} filterLabels={filterLabels} />
    </ReportView>
  );
};

export default ReliefBoardReportPage;
